import React, { useEffect, useRef, useState } from "react";
import {
  Tabs,
  Tab,
  Table,
  Button,
  Pagination,
  Overlay,
  Tooltip,
} from "react-bootstrap";
import theme from "../../styles/theme/theme";
import {
  FaExchangeAlt,
  FaGavel,
  FaTags,
  FaChevronLeft,
  FaChevronRight,
  FaDollarSign,
  FaBox,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUser,
  FaLayerGroup,
  FaCubes,
  FaFileAlt,
  FaCheckCircle,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/common/Loader";
import {
  acceptBid,
  getMarketData,
  rejectBid,
} from "../../features/buyer/buyerSlice";
import { switchRole, fetchUserAddresses } from "../../features/user/userSlice";
import { setCredentials } from "../../features/auth/authSlice";
import { displayValue } from "../../utils/displayValue";
import PlaceBidModal from "../../components/buyer/PlaceBidModal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AcceptOfferModal from "../../components/buyer/AcceptOfferModal";
import RejectOfferModal from "../../components/buyer/RejectOfferModal";
import CustomAlert from "../../components/common/CustomAlert";
import { timeAgo } from "../../utils/timeAgo";
import { Box, Container } from "@mui/material";
import Seo from "../../components/Seo";
import Breadcrumb from "../../components/Breadcrumb";
import { generateBreadcrumbs } from "../../utils/breadcrumbs";
import {
  formatLocalDate,
  formatLocalTime,
  getUserTimezoneInfo,
} from "../../utils/timeUtils";
// import HoverableTooltip from "../../components/buyer/HoverableTooltip";
import LoginRegisterModal from "../../components/buyer/LoginRegisterModal";
import VerifySwitchRoleModal from "../../components/buyer/VerifySwitchRoleModal";
import MarketPage from "./MarketPage";
import CTAHomeSection from "./CTAHomeSection";
import { formatCurrency, formatPrice } from "../../utils/price";
import KycBanner from "../../components/common/KycBanner";
import { capitalizeFirst } from "../../utils/stringUtils";

/* ------------------------- Helpers (theme + color) ------------------------- */
const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const bigint = parseInt(c, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const tp = theme?.palette || {};
const primary = tp.primary?.main || "#0d6efd";
const secondary = tp.secondary?.main || "#6c5ce7";
const success = tp.success?.main || "#28a745";
const warning = tp.warning?.main || "#ffc107";
const error = tp.error?.main || "#dc3545";
const textPrimary = tp.text?.primary || "#1a1a1a";
const textSecondary = tp.text?.secondary || "#4b5563";
const surface = tp.background?.paper || "#ffffff";

/* ---------------------------- ToolTip Content ---------------------------- */
const HoverableTooltip = ({
  target,
  item,
  isLoggedIn,
  onRequireLogin,
  show,
  setHoveredRow,
  onClose,
  tabType,
}) => {
  const handleExploreClick = () => {
    if (isLoggedIn) window.open(`/product/${item.id}`, "_blank");
    else onRequireLogin();
  };

  const details = [
    { label: "Commodities", value: item?.name, icon: <FaBox /> },
    { label: "Category", value: item?.category?.name, icon: <FaLayerGroup /> },
    // {
    //   label: "Material Type",
    //   value: item?.material_type || "N/A",
    //   icon: <FaCubes />,
    // },
    (item?.quantity || item?.product?.quantity) && {
      label: "Quantity",
      value: item.quantity || item.product?.quantity,
      icon: <FaBox />,
    },
    (item?.price != null || item?.bid_price != null) && {
      label: "Price",
      value: formatCurrency(item.price ?? item.bid_price, item.currency),
      icon: <FaDollarSign />,
    },
    tabType === "bids" &&
    item?.minimum_asking_price != null && {
      label: "Min Asking Price",
      value: formatCurrency(item.minimum_asking_price, item.currency),
      icon: <FaDollarSign />,
    },
    tabType === "bids" &&
    item?.last_bid_price != null && {
      label: "Last Bid",
      value: formatCurrency(item.last_bid_price, item.currency),
      icon: <FaDollarSign />,
    },
    tabType === "offers" &&
    item?.max_buying_price != null && {
      label: "Max Buying Price",
      value: formatCurrency(item.max_buying_price, item.currency),
      icon: <FaDollarSign />,
    },
    item?.best_offer && {
      label: "Best Offer",
      value: formatCurrency(item.best_offer, item.currency),
      icon: <FaDollarSign />,
    },
    item?.location && {
      label: "Location",
      value: item.location,
      icon: <FaMapMarkerAlt />,
    },
    item?.bids?.created_at && {
      label: "Bid Created Date",
      value: item.bids.created_at,
      icon: <FaCalendarAlt />,
    },
    (item?.seller?.name || item?.buyer_name || item?.created_by) && {
      label: "Created By",
      value: item?.seller?.name || item?.buyer_name || item?.created_by,
      icon: <FaUser />,
    },
    item?.seller?.company_name && {
      label: "Seller",
      value: item.seller.company_name,
      icon: <FaUser />,
    },
    item?.created_at && {
      label: "Created At",
      value: timeAgo(item.created_at),
      icon: <FaCalendarAlt />,
    },
  ].filter(Boolean);

  return (
    <Overlay
      target={target}
      show={show}
      placement="top"
      popperConfig={{
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, -12] }, // fixed clean offset
          },
        ],
      }}
    >
      {(props) => (
        <div
          {...props}
          style={{
            zIndex: 9999,
            ...props.style,
            pointerEvents: "auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            id="floating-tooltip"
            style={{
              position: "relative",
              fontSize: "0.85rem",
              width: "auto",
              minWidth: 320,
              maxWidth: 420,
              display: "flex",
              flexDirection: "column",
              background: "rgba(18, 18, 18, 0.95)",
              backdropFilter: "blur(12px)",
              color: "#fff",
              borderRadius: 16,
              padding: "20px 24px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "left",
            }}
          >
            {/* Arrow */}
            <div
              style={{
                position: "absolute",
                bottom: -8,
                left: "50%",
                transform: "translateX(-50%) rotate(45deg)",
                width: 16,
                height: 16,
                background: "#121212",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                zIndex: -1,
              }}
            />

            {/* Details */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {details.map((d, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  {d.icon && (
                    <span
                      style={{
                        color: "#888",
                        marginTop: "2px",
                        minWidth: "16px",
                      }}
                    >
                      {d.icon}
                    </span>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      gap: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.85rem",
                        color: "#888",
                        fontWeight: 600,
                        minWidth: "130px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {d.label}:
                    </span>
                    <span
                      style={{
                        fontWeight: 500,
                        wordBreak: "break-word",
                        color: "#fff",
                        fontSize: "0.85rem",
                        flex: 1,
                      }}
                    >
                      {d.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Explore Button */}
            {tabType !== "offers" && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Button
                  size="sm"
                  variant="primary"
                  style={{
                    borderRadius: "9999px",
                    padding: "6px 18px",
                    background: "linear-gradient(90deg, #4f46e5, #3b82f6)",
                    color: "#fff",
                    fontWeight: 500,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "scale(1.05)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                  onClick={handleExploreClick}
                >
                  Explore
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Overlay>
  );
};

/* ---------------------------- Trades Tab ---------------------------- */
const TradesTab = ({
  trades,
  navigate,
  total,
  page,
  setPage,
  pageSize,
  isLoggedIn,
  onRequireLogin,
  showLoginModal,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [hoveredRow, setHoveredRow] = useState(null);
  const [cursorPos, setCursorPos] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => {
    if (showLoginModal) {
      setHoveredRow(null);
    }
  }, [showLoginModal]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Scrollable table container */}
      <div style={{ overflowY: "auto" }}>
        <Table hover responsive className="glass-table">
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 2,
            }}
          >
            <tr>
              <th style={{ minWidth: "150px" }}>Commodities</th>
              <th style={{ minWidth: "120px" }}>Category</th>
              <th style={{ minWidth: "150px" }}>Location</th>
              <th style={{ minWidth: "80px" }}>Quantity</th>
              <th style={{ minWidth: "100px" }}>Price</th>
              <th style={{ minWidth: "120px" }}>Time</th>
              <th style={{ minWidth: "120px" }}>Created By</th>
            </tr>
          </thead>
          <tbody>
            {console.log("trades:", trades)}

            {trades?.length > 0 ? (
              trades.map((trade) => (
                <tr
                  key={trade.id}
                  ref={(el) => (rowRefs.current[trade.id] = el)}
                  onMouseEnter={() =>
                    setHoveredRow({
                      id: trade.id,
                      item: trade.product || trade,
                    })
                  }
                  onMouseLeave={(e) => {
                    const to = e.relatedTarget;
                    const isNode =
                      to && (to instanceof Node || to.nodeType === 1);
                    const insideRow =
                      isNode && rowRefs.current[trade.id]?.contains(to);
                    const tooltipEl =
                      document.getElementById("floating-tooltip");
                    const insideTooltip = isNode && tooltipEl?.contains(to);
                    if (!insideRow && !insideTooltip) {
                      setHoveredRow(null);
                    }
                  }}
                  onClick={() =>
                    trade.order_id &&
                    navigate(`/order-details/${trade.order_id}`)
                  }
                  className="clickable-row"
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ minWidth: "150px" }}>
                    {trade.order_id ? (
                      <Link
                        to={`/order-details/${trade.order_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          textDecoration: "none",
                          color: "#0d6efd",
                          fontWeight: 600,
                        }}
                      >
                        {displayValue(trade.product?.name)}
                      </Link>
                    ) : (
                      displayValue(trade.product?.name)
                    )}
                  </td>

                  <td style={{ minWidth: "120px" }}>
                    {displayValue(trade.product?.category?.name)}
                  </td>

                  <td style={{ minWidth: "150px" }}>
                    {displayValue(trade.product?.location)}
                  </td>
                  <td style={{ minWidth: "80px" }}>
                    {trade.product?.quantity != null &&
                      trade.product?.unit_of_measurement
                      ? `${displayValue(trade.product?.quantity)} ${trade.product?.unit_of_measurement
                      }`
                      : displayValue(trade.product?.quantity)}
                  </td>
                  {/* {console.log("price:", trade.formatted_price)} */}
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(
                      trade.formatted_price,
                      trade.product?.currency,
                    )}
                    {/* {displayValue(trade.formatted_price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>

                  <td style={{ minWidth: "120px" }}>
                    {displayValue(trade.time_ago)}
                  </td>

                  <td style={{ minWidth: "120px" }}>
                    {displayValue(trade.seller_name)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  No trades available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Tooltip rendered globally — NOT wrapping TDs */}
      {hoveredRow && !showLoginModal && (
        <HoverableTooltip
          target={rowRefs.current[hoveredRow.id]}
          item={hoveredRow.item}
          isLoggedIn={isLoggedIn}
          onRequireLogin={onRequireLogin}
          show={!showLoginModal}
          onClose={() => setHoveredRow(null)}
          tabType="trades"
        />
      )}

      {/* Pagination - always below scrollable area */}
      {total > 5 && (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 8 }}
        >
          <Pagination className="custom-pagination" data-type="trades">
            <Pagination.Prev
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              <FaChevronLeft />
            </Pagination.Prev>

            {[...Array(totalPages).keys()].map((num) => (
              <Pagination.Item
                key={num + 1}
                active={num + 1 === page}
                onClick={() => setPage(num + 1)}
              >
                {num + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              <FaChevronRight />
            </Pagination.Next>
          </Pagination>
        </div>
      )}
    </div>
  );
};

/* ----------------------------- Bids Tab ----------------------------- */
const BidsTab = ({
  bids,
  isLoggedIn,
  onPlaceBid,
  total,
  page,
  setPage,
  pageSize,
  onRequireLogin,
  showLoginModal,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [hoveredRow, setHoveredRow] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => {
    if (showLoginModal) {
      setHoveredRow(null);
    }
  }, [showLoginModal]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Scrollable table container */}
      <div style={{ overflowY: "auto" }}>
        <Table hover responsive className="glass-table">
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 2,
            }}
          >
            <tr>
              <th style={{ minWidth: "150px" }}>Commodities</th>
              <th style={{ minWidth: "120px" }}>Category</th>
              <th style={{ minWidth: "80px" }}>Quantity</th>
              <th style={{ minWidth: "100px" }}>Price</th>
              <th style={{ minWidth: "100px" }}>Min Asking Price</th>
              <th style={{ minWidth: "100px" }}>Last Bid</th>
              <th style={{ minWidth: "100px" }}>Best Offer</th>
              <th style={{ minWidth: "150px" }}>Bid Created Date</th>
              <th style={{ minWidth: "120px" }}>Created By</th>
              <th style={{ minWidth: "120px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {bids?.length > 0 ? (
              bids.map((bid) => (
                <tr
                  key={bid.id}
                  ref={(el) => (rowRefs.current[bid.id] = el)}
                  onMouseEnter={() =>
                    setHoveredRow({
                      id: bid.id,
                      item: bid, // <-- full bid object passed to tooltip
                    })
                  }
                  onMouseLeave={(e) => {
                    const to = e.relatedTarget;
                    const isNode =
                      to && (to instanceof Node || to.nodeType === 1);
                    const insideRow =
                      isNode && rowRefs.current[bid.id]?.contains(to);
                    const tooltipEl =
                      document.getElementById("floating-tooltip");
                    const insideTooltip = isNode && tooltipEl?.contains(to);
                    if (!insideRow && !insideTooltip) setHoveredRow(null);
                  }}
                  className="clickable-row"
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ minWidth: "150px" }}>
                    {displayValue(bid.name)}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {displayValue(bid.category?.name)}
                  </td>
                  <td style={{ minWidth: "80px" }}>
                    {bid.quantity != null && bid.unit_of_measurement
                      ? `${displayValue(bid.quantity)} ${bid.unit_of_measurement
                      }`
                      : displayValue(bid.quantity)}
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(bid.price, bid.currency)}
                    {/* {displayValue(bid.price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(bid.minimum_asking_price, bid.currency)}
                    {/* {displayValue(bid.price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(bid.last_bid_price, bid.currency)}
                    {/* {displayValue(bid.price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(bid.best_offer, bid.currency)}
                    {/* {displayValue(bid.best_offer, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>

                  <td style={{ minWidth: "150px" }}>
                    {displayValue(timeAgo(bid.created_at))}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {displayValue(bid.seller_name)}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {bid.actions?.can_place_bid && isLoggedIn ? (
                      <Button
                        size="sm"
                        style={{
                          background: `linear-gradient(180deg, ${hexToRgba(
                            "#f7eef8",
                            0.95,
                          )} 0%, ${hexToRgba("#f0d9ff", 0.95)} 100%)`,
                          borderRadius: "9999px",
                          padding: "6px 16px",
                          border: `1px solid ${hexToRgba("#ffffff", 0.25)}`,
                          color: "black",
                          boxShadow: `0 8px 18px ${hexToRgba("#7c3aed", 0.25)}`,
                        }}
                        onClick={() => onPlaceBid(bid)}
                      >
                        Place Bid
                      </Button>
                    ) : (
                      <span style={{ fontSize: "0.85rem" }}>N/A</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} style={{ textAlign: "center" }}>
                  No bids available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Tooltip rendered globally — NOT wrapping TDs */}
      {hoveredRow && !showLoginModal && (
        <HoverableTooltip
          target={rowRefs.current[hoveredRow.id]}
          item={hoveredRow.item}
          isLoggedIn={isLoggedIn}
          onRequireLogin={onRequireLogin}
          show={!showLoginModal}
          onClose={() => setHoveredRow(null)}
          tabType="bids"
        />
      )}

      {/* Pagination - always below scrollable area */}
      {total > 5 && (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 8 }}
        >
          <Pagination className="custom-pagination" data-type="bids">
            <Pagination.Prev
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              <FaChevronLeft />
            </Pagination.Prev>

            {[...Array(totalPages).keys()].map((num) => (
              <Pagination.Item
                key={num + 1}
                active={num + 1 === page}
                onClick={() => setPage(num + 1)}
              >
                {num + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              <FaChevronRight />
            </Pagination.Next>
          </Pagination>
        </div>
      )}
    </div>
  );
};

/* ---------------------------- Offers Tab ---------------------------- */
const OffersTab = ({
  offers,
  onAccept,
  onReject,
  total,
  page,
  setPage,
  pageSize,
  isLoggedIn,
  onRequireLogin,
  showLoginModal,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [hoveredRow, setHoveredRow] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => {
    if (showLoginModal) {
      setHoveredRow(null);
    }
  }, [showLoginModal]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Scrollable table container */}
      <div style={{ overflowY: "auto" }}>
        <Table hover responsive className="glass-table">
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 2,
            }}
          >
            <tr>
              <th style={{ minWidth: "150px" }}>Commodities</th>
              <th style={{ minWidth: "120px" }}>Category</th>
              <th style={{ minWidth: "150px" }}>Location</th>
              <th style={{ minWidth: "80px" }}>Quantity</th>
              <th style={{ minWidth: "100px" }}>Price</th>
              <th style={{ minWidth: "120px" }}>Best Offer</th>
              <th style={{ minWidth: "120px" }}>Time</th>
              <th style={{ minWidth: "120px" }}>Created By</th>
              <th style={{ minWidth: "160px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {offers?.length > 0 ? (
              offers.map((offer) => (
                <tr
                  key={offer.id}
                  ref={(el) => (rowRefs.current[offer.id] = el)}
                  onMouseEnter={() =>
                    setHoveredRow({
                      id: offer.id,
                      item: offer.product || offer, // tooltip item
                    })
                  }
                  onMouseLeave={(e) => {
                    const to = e.relatedTarget;
                    const isNode =
                      to && (to instanceof Node || to.nodeType === 1);
                    const insideRow =
                      isNode && rowRefs.current[offer.id]?.contains(to);
                    const tooltipEl =
                      document.getElementById("floating-tooltip");
                    const insideTooltip = isNode && tooltipEl?.contains(to);
                    if (!insideRow && !insideTooltip) setHoveredRow(null);
                  }}
                  className="clickable-row"
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ minWidth: "150px" }}>
                    {displayValue(offer.product?.name)}
                  </td>

                  <td style={{ minWidth: "120px" }}>
                    {displayValue(offer.product?.category?.name)}
                  </td>
                  <td style={{ minWidth: "150px" }}>
                    {displayValue(offer.location)}
                  </td>
                  <td style={{ minWidth: "80px" }}>
                    {offer.product?.quantity &&
                      offer.product?.unit_of_measurement
                      ? `${displayValue(offer.product.quantity)} ${offer.product.unit_of_measurement
                      }`
                      : displayValue(offer.product?.quantity)}
                  </td>
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(offer.bid_price, offer.product?.currency)}
                    {/* {displayValue(offer.bid_price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {formatCurrency(
                      offer.counter_price,
                      offer.product?.currency,
                    )}

                    {/* {displayValue(offer.counter_price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                      fallback: "N/A",
                    })} */}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {displayValue(timeAgo(offer.created_at))}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {displayValue(offer.created_by)}
                  </td>
                  <td style={{ minWidth: "160px" }}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {offer.actions?.can_accept && (
                        <Button
                          size="sm"
                          onClick={() => onAccept(offer)}
                          style={{
                            background: `linear-gradient(135deg, ${hexToRgba(
                              success,
                              0.95,
                            )} 0%, ${hexToRgba("#34d399", 0.95)} 100%)`,
                            borderRadius: "9999px",
                            padding: "6px 14px",
                            border: `1px solid ${hexToRgba("#ffffff", 0.25)}`,
                            color: "white",
                            boxShadow: `0 8px 18px ${hexToRgba(success, 0.25)}`,
                          }}
                        >
                          Accept
                        </Button>
                      )}
                      {offer.actions?.can_reject && (
                        <Button
                          size="sm"
                          onClick={() => onReject(offer)}
                          style={{
                            background: `linear-gradient(135deg, ${hexToRgba(
                              error,
                              0.95,
                            )} 0%, ${hexToRgba("#ef4444", 0.95)} 100%)`,
                            borderRadius: "9999px",
                            padding: "6px 14px",
                            border: `1px solid ${hexToRgba("#ffffff", 0.25)}`,
                            color: "white",
                            boxShadow: `0 8px 18px ${hexToRgba(error, 0.25)}`,
                          }}
                        >
                          Reject
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} style={{ textAlign: "center" }}>
                  No offers available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Tooltip rendered globally — NOT wrapping TDs */}
      {hoveredRow && !showLoginModal && (
        <HoverableTooltip
          target={rowRefs.current[hoveredRow.id]}
          item={hoveredRow.item}
          isLoggedIn={isLoggedIn}
          onRequireLogin={onRequireLogin}
          show={!showLoginModal}
          onClose={() => setHoveredRow(null)}
        />
      )}

      {/* Pagination - always below scrollable area */}
      {total > 5 && (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 8 }}
        >
          <Pagination className="custom-pagination" data-type="offers">
            <Pagination.Prev
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              <FaChevronLeft />
            </Pagination.Prev>

            {[...Array(totalPages).keys()].map((num) => (
              <Pagination.Item
                key={num + 1}
                active={num + 1 === page}
                onClick={() => setPage(num + 1)}
              >
                {num + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              <FaChevronRight />
            </Pagination.Next>
          </Pagination>
        </div>
      )}
    </div>
  );
};

/* ---------------------------- Buyers Offers Tab ---------------------------- */
const BuyersOffersTab = ({
  all_offers,
  onAccept,
  onReject,
  onPlaceBid,
  total,
  page,
  setPage,
  pageSize,
  isLoggedIn,
  user,
  onRequireLogin,
  showLoginModal,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [hoveredRow, setHoveredRow] = useState(null);
  const rowRefs = useRef({});
  console.log('all_offers:', all_offers)

  useEffect(() => {
    if (showLoginModal) {
      setHoveredRow(null);
    }
  }, [showLoginModal]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Scrollable table container */}
      <div style={{ overflowY: "auto" }}>
        <Table hover responsive className="glass-table">
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 2,
            }}
          >
            <tr>
              <th style={{ minWidth: "150px" }}>Commodities</th>
              <th style={{ minWidth: "120px" }}>Category</th>
              <th style={{ minWidth: "150px" }}>Location</th>
              <th style={{ minWidth: "80px" }}>Quantity</th>
              {/* <th style={{ minWidth: "100px" }}>Price</th> */}
              <th style={{ minWidth: "100px" }}>Max Buying Price</th>
              {/* <th style={{ minWidth: "120px" }}>Best Offer</th> */}
              <th style={{ minWidth: "120px" }}>Time</th>
              <th style={{ minWidth: "120px" }}>Created By</th>
              <th style={{ minWidth: "160px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {all_offers?.length > 0 ? (
              all_offers.map((offer) => (
                <tr
                  key={offer.id}
                  ref={(el) => (rowRefs.current[offer.id] = el)}
                  onMouseEnter={() =>
                    setHoveredRow({
                      id: offer.id,
                      item: offer, // tooltip item
                    })
                  }
                  onMouseLeave={(e) => {
                    const to = e.relatedTarget;
                    const isNode =
                      to && (to instanceof Node || to.nodeType === 1);
                    const insideRow =
                      isNode && rowRefs.current[offer.id]?.contains(to);
                    const tooltipEl =
                      document.getElementById("floating-tooltip");
                    const insideTooltip = isNode && tooltipEl?.contains(to);
                    if (!insideRow && !insideTooltip) setHoveredRow(null);
                  }}
                  className="clickable-row"
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ minWidth: "150px" }}>
                    {displayValue(offer.name)}
                  </td>

                  <td style={{ minWidth: "120px" }}>
                    {displayValue(offer.category?.name)}
                  </td>
                  <td style={{ minWidth: "150px" }}>
                    {displayValue(offer.location)}
                  </td>
                  <td style={{ minWidth: "80px" }}>
                    {offer.quantity &&
                      offer.unit_of_measurement
                      ? `${displayValue(offer.quantity)} ${offer.unit_of_measurement
                      }`
                      : displayValue(offer.quantity)}
                  </td>
                  {/* <td style={{ minWidth: "100px" }}>
                    {formatCurrency(offer.bid_price, offer.currency)} */}
                  {/* {displayValue(offer.bid_price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  {/* </td> */}
                  <td style={{ minWidth: "100px" }}>
                    {formatCurrency(offer.max_buying_price, offer.currency)}
                    {/* {displayValue(offer.bid_price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                    })} */}
                  </td>
                  {/* <td style={{ minWidth: "120px" }}>
                    {formatCurrency(
                      offer.counter_price,
                      offer.product?.currency,
                    )} */}

                  {/* {displayValue(offer.counter_price, {
                      prefix: "A$",
                      formatFn: formatPrice,
                      fallback: "N/A",
                    })} */}
                  {/* </td> */}
                  <td style={{ minWidth: "120px" }}>
                    {displayValue(timeAgo(offer.created_at))}
                  </td>
                  <td style={{ minWidth: "120px" }}>
                    {displayValue(capitalizeFirst(offer.buyer_name))}
                  </td>
                  {console.log('Is Mine:', offer.buyer_id, user?.id)}
                  <td style={{ minWidth: "160px" }}>
                    {!isLoggedIn ? (
                      <span style={{ fontSize: "0.85rem" }}>N/A</span>
                    ) : (offer.is_mine || (user?.id && offer.buyer_id === user.id)) ? (
                      <span className="text-muted" style={{ fontSize: "0.85rem", fontStyle: "bold", fontWeight: 800 }}>
                        Your Listing
                      </span>
                    ) : (
                      <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlaceBid(offer);
                          }}
                          style={{
                            background: `linear-gradient(135deg, ${hexToRgba(
                              secondary,
                              0.95,
                            )} 0%, ${hexToRgba("#818cf8", 0.95)} 100%)`,
                            borderRadius: "9999px",
                            padding: "6px 14px",
                            border: `1px solid ${hexToRgba("#ffffff", 0.25)}`,
                            color: "white",
                            boxShadow: `0 8px 18px ${hexToRgba(secondary, 0.25)}`,
                          }}
                        >
                          Make Offer
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} style={{ textAlign: "center" }}>
                  No offers available.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Tooltip rendered globally — NOT wrapping TDs */}
      {hoveredRow && !showLoginModal && (
        <HoverableTooltip
          target={rowRefs.current[hoveredRow.id]}
          item={hoveredRow.item}
          isLoggedIn={isLoggedIn}
          onRequireLogin={onRequireLogin}
          show={!showLoginModal}
          onClose={() => setHoveredRow(null)}
          tabType="offers"
        />
      )}

      {/* Pagination - always below scrollable area */}
      {total > 5 && (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 8 }}
        >
          <Pagination className="custom-pagination" data-type="offers">
            <Pagination.Prev
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              <FaChevronLeft />
            </Pagination.Prev>

            {[...Array(totalPages).keys()].map((num) => (
              <Pagination.Item
                key={num + 1}
                active={num + 1 === page}
                onClick={() => setPage(num + 1)}
              >
                {num + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              <FaChevronRight />
            </Pagination.Next>
          </Pagination>
        </div>
      )}
    </div>
  );
};

/* --------------------------- Main Component ------------------------- */
const Market = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const marketState = useSelector((state) => state.buyer) || {};
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromURL = queryParams.get("tab");

  const {
    trades = [],
    offers = [],
    bids = [],
    all_offers = [],
    loading = false,
    error = null,
  } = marketState;
  const { token, role, roles, user } = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(token);
  // console.log('Token:', token)
  // console.log('IsLoggedIn:', isLoggedIn)

  const [activeTab, setActiveTab] = useState(tabFromURL || "bids");
  const [showModal, setShowModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [alert, setAlert] = useState({ message: "", type: "", visible: false });
  const [showVerifyRoleModal, setShowVerifyRoleModal] = useState(false);
  const [clock, setClock] = useState(new Date());

  // Role Switch States
  const [pendingOffer, setPendingOffer] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);
  // Filters state
  const [tabFilters, setTabFilters] = useState({
    trades: {
      location: "",
      commodity: "",
      category: "",
      sort: "latest",
      pageSize: 20,
    },
    bids: {
      location: "",
      commodity: "",
      category: "",
      sort: "latest",
      pageSize: 20,
    },
    offers: {
      location: "",
      commodity: "",
      category: "",
      sort: "latest",
      pageSize: 20,
    },
    all_offers: {
      location: "",
      commodity: "",
      category: "",
      sort: "latest",
      pageSize: 20,
    },
  });

  // Pagination states
  const [tradePage, setTradePage] = useState(1);
  const [bidPage, setBidPage] = useState(1);
  const [offerPage, setOfferPage] = useState(1);
  const [buyersOffersPage, setBuyersOffersPage] = useState(1);

  const [showLoginModal, setShowLoginModal] = useState(false);
  // useEffect(() => {
  //   if (showLoginModal) {
  //     setHoveredRow(null); // 🔥 FORCE CLOSE TOOLTIP
  //   }
  // }, [showLoginModal]);

  const handleRequireLogin = () => {
    setShowLoginModal(true);
  };

  const handleRoleSwitch = async (targetRole) => {
    setIsSwitching(true);
    try {
      const result = await dispatch(switchRole({ role: targetRole })).unwrap();

      if (targetRole === "seller") {
        try {
          const addressRes = await dispatch(fetchUserAddresses()).unwrap();
          const addressList = addressRes?.data || addressRes;
          const addressCount = Array.isArray(addressList) ? addressList.length : 0;

          const freshUser = result?.data?.user || result?.user;
          const updatedRoles = roles?.includes(targetRole)
            ? roles
            : [...(roles || []), targetRole];
          const mergedUser = { ...user, ...(freshUser || {}), addresses_count: addressCount, roles: updatedRoles };

          if (mergedUser) {
            dispatch(
              setCredentials({
                user: mergedUser,
                role: targetRole,
                roles: updatedRoles,
              })
            );
          }
        } catch (addrErr) {
          console.error("Error fetching addresses during switch:", addrErr);
        }
      }

      // After role switch, if there was a pending offer, open the bid modal
      if (pendingOffer) {
        setSelectedBid(pendingOffer);
        setShowModal(true);
        setPendingOffer(null);
      } else if (targetRole === "seller") {
        // If no pending offer but switching to seller, go to dashboard
        navigate("/seller/dashboard");
      }
    } catch (error) {
      console.error("Role switch failed:", error);
    } finally {
      setIsSwitching(false);
    }
  };

  const handleConfirmRoleSwitch = () => {
    handleRoleSwitch("seller");
  };

  const handleMakeOffer = () => {
    console.log("🔍 Debug Info (Market):", { isLoggedIn, role, roles, userRoles: user?.roles });

    if (!isLoggedIn) {
      navigate("/login", { state: { role: "seller" } });
      return;
    }

    // Identify if user is a seller from any available state
    const isActuallySeller =
      role === "seller" ||
      roles?.includes("seller") ||
      (Array.isArray(user?.roles) && user.roles.includes("seller")) ||
      (typeof user?.roles === 'string' && user.roles.includes("seller"));

    // Already a seller -> go to dashboard or switch role if not active
    if (isActuallySeller) {
      if (role !== "seller") {
        console.log("🔄 User is seller but active role is not. Switching...");
        handleRoleSwitch("seller");
      } else {
        console.log("✅ Navigating to seller dashboard");
        navigate("/seller/dashboard");
      }
      return;
    }

    // Not a seller yet -> show activation modal
    console.log("❌ Showing activation modal");
    setShowVerifyRoleModal(true);
  };

  const handlePlaceBid = (offer) => {
    setSelectedBid(offer);
    setShowModal(true);
  };
  const pageSize = 20; // rows per page

  const applyFilters = (data, filters) => {
    let filtered = [...data];

    // Filter by commodity
    if (filters.commodity) {
      filtered = filtered.filter(
        (item) =>
          item.product?.name === filters.commodity ||
          item.name === filters.commodity,
      );
    }

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(
        (item) =>
          item.product?.category?.name === filters.category ||
          item.category?.name === filters.category,
      );
    }

    // Filter by location
    if (filters.location) {
      filtered = filtered.filter(
        (item) =>
          item.product?.location === filters.location ||
          item.location === filters.location,
      );
    }

    // Sort
    if (filters.sort === "latest")
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (filters.sort === "oldest")
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (filters.sort === "price_high")
      filtered.sort(
        (a, b) =>
          (b.product?.price || b.price || 0) -
          (a.product?.price || a.price || 0),
      );
    else if (filters.sort === "price_low")
      filtered.sort(
        (a, b) =>
          (a.product?.price || a.price || 0) -
          (b.product?.price || b.price || 0),
      );

    return filtered;
  };

  const paginatedTrades = applyFilters(trades, tabFilters.trades).slice(
    (tradePage - 1) * tabFilters.trades.pageSize,
    tradePage * tabFilters.trades.pageSize,
  );

  const paginatedBids = applyFilters(bids, tabFilters.bids).slice(
    (bidPage - 1) * tabFilters.bids.pageSize,
    bidPage * tabFilters.bids.pageSize,
  );

  // console.log("tabFilters", tabFilters);
  // console.log("bids", bids);
  // console.log("paginatedBids", paginatedBids);

  const paginatedOffers = applyFilters(offers, tabFilters.offers).slice(
    (offerPage - 1) * tabFilters.offers.pageSize,
    offerPage * tabFilters.offers.pageSize,
  );

  const paginatedBuyersOffers = applyFilters(
    all_offers,
    tabFilters.all_offers,
  ).slice(
    (buyersOffersPage - 1) * tabFilters.all_offers.pageSize,
    buyersOffersPage * tabFilters.all_offers.pageSize,
  );

  useEffect(() => {
    dispatch(getMarketData());
  }, [dispatch, token]);

  useEffect(() => {
    const interval = setInterval(() => {
      setClock(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const breadcrumbs = generateBreadcrumbs({ pageType: "market" });

  const handleAcceptClick = (offer) => {
    setSelectedOffer(offer);
    setShowAcceptModal(true);
  };

  const handleRejectClick = (offer) => {
    setSelectedOffer(offer);
    setShowRejectModal(true);
  };

  const handleAcceptSubmit = async (offerId) => {
    try {
      await dispatch(acceptBid(offerId)).unwrap();
      setAlert({
        message: "Offer accepted successfully.",
        type: "success",
        visible: true,
      });
      dispatch(getMarketData());
    } catch {
      setAlert({
        message: "Failed to accept offer.",
        type: "danger",
        visible: true,
      });
    } finally {
      setShowAcceptModal(false);
    }
  };

  const handleRejectSubmit = async ({ offerId, reason }) => {
    try {
      await dispatch(rejectBid({ id: offerId, reason })).unwrap();
      setAlert({
        message: "Offer rejected successfully.",
        type: "warning",
        visible: true,
      });
      dispatch(getMarketData());
    } catch {
      setAlert({
        message: "Failed to reject offer.",
        type: "danger",
        visible: true,
      });
    } finally {
      setShowRejectModal(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [key]: value },
    }));

    // Reset pagination whenever filters change
    if (activeTab === "trades") setTradePage(1);
    if (activeTab === "bids") setBidPage(1);
    if (activeTab === "offers") setOfferPage(1);
    if (activeTab === "all_offers") setBuyersOffersPage(1);
  };

  const selectStyle = {
    padding: "10px 14px", // text padding
    borderRadius: "12px",
    border: `1px solid ${hexToRgba(primary, 0.4)}`,
    background: `${hexToRgba(
      "#fff",
      0.7,
    )} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='${encodeURIComponent(
      "#000000",
    )}' d='M7 10l5 5 5-5H7z'/%3E%3C/svg%3E") no-repeat right 12px center`,
    backgroundSize: "16px 16px", // arrow size
    color: "#000000",
    fontWeight: 500,
    appearance: "none", // remove native arrow
    WebkitAppearance: "none",
    MozAppearance: "none",
  };

  const selectStylePageSize = {
    ...selectStyle,
    padding: "8px 12px",
    borderRadius: "10px",
    minWidth: "100px",
  };

  const getUniqueOptions = (data, keyPath) => {
    return [
      ...new Set(
        data
          .map((item) => {
            // Support nested keys like "product.category.name"
            return keyPath.split(".").reduce((obj, key) => obj?.[key], item);
          })
          .filter(Boolean),
      ),
    ];
  };

  const { tzName, tzAbbr } = getUserTimezoneInfo();

  const dynamicFilterOptions = {
    trades: {
      commodity: getUniqueOptions(trades, "product.name"),
      category: getUniqueOptions(trades, "product.category.name"),
      location: getUniqueOptions(trades, "product.location"),
    },
    bids: {
      commodity: getUniqueOptions(bids, "name"),
      category: getUniqueOptions(bids, "category.name"),
      location: getUniqueOptions(bids, "location"),
    },
    offers: {
      commodity: getUniqueOptions(offers, "product.name"),
      category: getUniqueOptions(offers, "product.category.name"),
      location: getUniqueOptions(offers, "location"),
    },
    all_offers: {
      commodity: getUniqueOptions(all_offers, "name"),
      category: getUniqueOptions(all_offers, "product.category.name"),
      location: getUniqueOptions(all_offers, "location"),
    },
  };

  if (loading) {
    return (
      <div
        style={{
          padding: theme.spacing(5),
          background: `
            radial-gradient(1000px circle at -10% -10%, ${hexToRgba(
            primary,
            0.12,
          )} 0, transparent 50%),
            radial-gradient(1000px circle at 110% -10%, ${hexToRgba(
            secondary,
            0.12,
          )} 0, transparent 50%),
            linear-gradient(135deg, #f8fafc, #e9ecef)
          `,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  if (error) return <p>Error: {error}</p>;

  return (
    <>
      {isSwitching && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
        </Box>
      )}
      <Seo
        title="Explore Scrap Material Market | ScrapTrade"
        description="Explore the live scrap market on ScrapTrade. View trades, place bids, and manage offers in real-time with verified buyers and sellers."
        url="https://scraptrade.com.au/market"
        canonical="https://scraptrade.com.au/market"
      />

      <div
        style={{
          padding: theme.spacing(6),
          background: `
            radial-gradient(1200px circle at -10% -10%, ${hexToRgba(
            primary,
            0.14,
          )} 0, transparent 55%),
            radial-gradient(1200px circle at 110% -10%, ${hexToRgba(
            secondary,
            0.16,
          )} 0, transparent 55%),
            linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%)
          `,
          minHeight: "100vh",
          position: "relative",
        }}
      >
        {/* Page header moved outside the main glass card */}
        <div
          style={{
            width: "100%",
            padding: "60px 20px",
            textAlign: "center",
            color: "white",
            background: `linear-gradient(145deg, ${hexToRgba(
              primary,
              0.9,
            )}, ${hexToRgba(textPrimary, 0.9)})`,
            // backgroundImage: "url('/images/market-banner.jpg')", // optional background image
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "12px",
            marginBottom: "24px",
          }}
        >
          <h1
            style={{
              fontWeight: "bold",
              color: "var(--mui-palette-text-primary)", // or just "inherit"
              marginBottom: "16px",
              fontSize: "clamp(2.5rem, 5vw, 3.5rem)", // responsive size
            }}
          >
            Scrap Market
          </h1>

          <p
            style={{
              maxWidth: "600px",
              margin: "0 auto",
              fontSize: "clamp(1rem, 2vw, 1.25rem)",
              color: "var(--mui-palette-text-secondary)",
            }}
          >
            Explore live trades, bids, and offers in real-time. Stay ahead with
            the latest market activity and make informed decisions instantly.
          </p>
        </div>

        <Container maxWidth="xl" sx={{ mt: 3, mb: 1 }}>
          <Breadcrumb items={breadcrumbs} />
        </Container>

        <KycBanner />

        <div
          className="glass-card"
          style={{
            maxWidth: "1500px",
            margin: "0 auto",
            background: `
              linear-gradient(180deg, ${hexToRgba(
              surface,
              0.5,
            )} 0%, ${hexToRgba(surface, 0.25)} 100%)
            `,
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            padding: theme.spacing(5),
            borderRadius: "24px",
            border: `1px solid ${hexToRgba("#ffffff", 0.35)}`,
            boxShadow: `
              0 10px 30px ${hexToRgba("#000000", 0.06)},
              inset 0 1px 0 ${hexToRgba("#ffffff", 0.4)}
            `,
            display: "flex",
            flexDirection: "column",
            minHeight: "56vh", // ensure container keeps height even when empty
          }}
        >
          <div className="card-header-gradient" />

          {/* Live clock */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              margin: "0 0 24px 0",
              width: "100%",
            }}
          >
            <span>{formatLocalDate(clock)}</span>
            <span>
              {formatLocalTime(clock)} {tzName} ({tzAbbr})
            </span>
          </div>

          {/* Dynamic header for current tab */}
          <div
            className="card-tab-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              marginBottom: theme.spacing(3),
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3
                style={{
                  ...(theme.typography?.h5 || {}),
                  margin: 0,
                  color: "#000000",
                  fontWeight: 800,
                }}
              >
                {activeTab === "trades"
                  ? "Trades"
                  : activeTab === "bids"
                    ? "Seller’s Bids"
                    : // : "Offers"
                    "Buyer’s Offers"}
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: "#000000",
                  fontSize: "0.95rem",
                  maxWidth: "720px",
                }}
              >
                {activeTab === "trades"
                  ? "Completed deals where a buyer and seller agreed on the price and quantity."
                  : activeTab === "bids"
                    ? "Scrap sellers post their material and the price they’re willing to sell at."
                    : // : "Manage incoming offers. Accept or reject offers and negotiate with buyers."
                    "Scrap buyers list what they want to buy and the price they’re ready to pay."}
              </p>
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <div
                style={{
                  padding: "6px 10px",
                  background: hexToRgba("#ffffff", 0.6),
                  borderRadius: 9999,
                  border: `1px solid ${hexToRgba("#ffffff", 0.5)}`,
                  fontWeight: 700,
                }}
              >
                {activeTab === "trades"
                  ? `${trades?.length || 0} Trades`
                  : activeTab === "bids"
                    ? `${bids?.length || 0} Bids`
                    : // : `${offers?.length || 0} offers`
                    `${all_offers?.length || 0} All Offers`}
              </div>
            </div>

            {/* Filters & Sorting */}
            <div
              style={{
                width: "100%",
                marginBottom: theme.spacing(3),
                padding: "16px 20px",
                background: `linear-gradient(180deg, ${hexToRgba(
                  surface,
                  0.7,
                )} 0%, ${hexToRgba(surface, 0.4)} 100%)`,
                borderRadius: "16px",
                border: `1px solid ${hexToRgba("#ffffff", 0.3)}`,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: `0 6px 18px ${hexToRgba("#000", 0.06)}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "20px",
                  width: "100%",
                }}
              >
                {/* Entries per page (start) */}
                <div
                  style={{
                    minWidth: "140px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* <h4
                    style={{
                      margin: "0 0 4px 0",  
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: textPrimary,
                    }}
                  >
                    Page Settings
                  </h4> */}
                  <label
                    style={{
                      fontSize: "0.85rem",
                      marginBottom: "4px",
                      color: "#000000",
                      fontWeight: 500,
                    }}
                  >
                    Entries per page
                  </label>
                  <select
                    value={tabFilters[activeTab].pageSize}
                    onChange={(e) =>
                      handleFilterChange("pageSize", parseInt(e.target.value))
                    }
                    style={selectStylePageSize}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* Filters (middle) */}
                <div
                  style={{
                    minWidth: "260px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#000000",
                    }}
                  >
                    Filters
                  </h4>
                  <div
                    style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}
                  >
                    {["commodity", "category", "location"].map((key) => (
                      <div
                        key={key}
                        style={{ display: "flex", flexDirection: "column" }}
                      >
                        <label
                          style={{
                            fontSize: "0.85rem",
                            marginBottom: "4px",
                            color: "#000000",
                            fontWeight: 500,
                          }}
                        >
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </label>
                        <select
                          value={tabFilters[activeTab][key]}
                          onChange={(e) =>
                            handleFilterChange(key, e.target.value)
                          }
                          style={{ ...selectStyle, width: "160px" }}
                        >
                          <option value="">All</option>
                          {dynamicFilterOptions[activeTab][key].map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sorting (end) */}
                <div
                  style={{
                    minWidth: "220px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* <h4
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: textPrimary,
                    }}
                  >
                    Sorting
                  </h4> */}
                  <label
                    style={{
                      fontSize: "0.85rem",
                      marginBottom: "4px",
                      color: "#000000",
                      fontWeight: 500,
                    }}
                  >
                    Sort By
                  </label>
                  <select
                    value={tabFilters[activeTab].sort}
                    onChange={(e) => handleFilterChange("sort", e.target.value)}
                    style={{ ...selectStyle, minWidth: "160px" }}
                  >
                    <option value="latest">Latest</option>
                    <option value="oldest">Oldest</option>
                    <option value="price_high">Price: High to Low</option>
                    <option value="price_low">Price: Low to High</option>
                  </select>
                </div>
              </div>
            </div>

            <div
              style={{
                width: "100%",
                marginBottom: 0,
                // No borderBottom here
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <Tabs
                  id="market-tabs"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="glass-tabs mb-0"
                  mountOnEnter
                  unmountOnExit
                >
                  <Tab
                    eventKey="bids"
                    title={
                      <span className="d-flex align-items-center gap-2">
                        <FaGavel /> Seller’s Bids
                      </span>
                    }
                    tabClassName="tab-bids"
                  />
                  <Tab
                    eventKey="trades"
                    title={
                      <span className="d-flex align-items-center gap-2">
                        <FaExchangeAlt /> Trades
                      </span>
                    }
                    tabClassName="tab-trades"
                  />

                  {/* <Tab
                    eventKey="offers"
                    title={
                      <span className="d-flex align-items-center gap-2">
                        <FaTags /> Offers
                      </span>
                    }
                    tabClassName="tab-offers"
                  /> */}
                  <Tab
                    eventKey="all_offers"
                    title={
                      <span className="d-flex align-items-center gap-2">
                        <FaTags /> Buyer’s Offers
                      </span>
                    }
                    tabClassName="tab-offers"
                  />
                </Tabs>
              </div>
            </div>
          </div>

          {/* Tab content below */}
          <div style={{ width: "100%", overflowX: "auto" }}>
            {activeTab === "trades" && (
              <TradesTab
                trades={paginatedTrades}
                isLoggedIn={isLoggedIn}
                navigate={navigate}
                total={trades.length}
                page={tradePage}
                setPage={setTradePage}
                pageSize={tabFilters.trades.pageSize}
                onRequireLogin={handleRequireLogin}
                showLoginModal={showLoginModal}
              />
            )}
            {activeTab === "bids" && (
              <BidsTab
                bids={paginatedBids}
                isLoggedIn={isLoggedIn}
                onPlaceBid={handlePlaceBid}
                total={bids.length}
                page={bidPage}
                setPage={setBidPage}
                pageSize={tabFilters.bids.pageSize}
                onRequireLogin={handleRequireLogin}
                showLoginModal={showLoginModal}
              />
            )}
            {activeTab === "offers" && (
              <OffersTab
                offers={paginatedOffers}
                isLoggedIn={isLoggedIn}
                onAccept={handleAcceptClick}
                onReject={handleRejectClick}
                total={offers.length}
                page={offerPage}
                setPage={setOfferPage}
                pageSize={tabFilters.offers.pageSize}
                onRequireLogin={handleRequireLogin}
                showLoginModal={showLoginModal}
              />
            )}

            {activeTab === "all_offers" && (
              <BuyersOffersTab
                all_offers={paginatedBuyersOffers}
                isLoggedIn={isLoggedIn}
                user={user}
                onAccept={handleAcceptClick}
                onReject={handleRejectClick}
                onPlaceBid={handleMakeOffer}
                total={all_offers.length}
                page={buyersOffersPage}
                setPage={setBuyersOffersPage}
                pageSize={tabFilters.all_offers.pageSize}
                onRequireLogin={handleRequireLogin}
                showLoginModal={showLoginModal}
              />
            )}
          </div>
        </div>
        <div className="mt-5">
          <MarketPage />
        </div>
        <div className="mt-5">
          <CTAHomeSection />
        </div>
      </div>

      <PlaceBidModal
        show={showModal}
        onHide={() => setShowModal(false)}
        product={selectedBid}
      />
      {showAcceptModal && selectedOffer && (
        <AcceptOfferModal
          show={showAcceptModal}
          onHide={() => setShowAcceptModal(false)}
          offer={selectedOffer}
          onSubmit={() => handleAcceptSubmit(selectedOffer.id)}
        />
      )}
      {showRejectModal && selectedOffer && (
        <RejectOfferModal
          show={showRejectModal}
          onHide={() => setShowRejectModal(false)}
          offer={selectedOffer}
          onSubmit={(data) => handleRejectSubmit(data)}
        />
      )}
      {alert.visible && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ ...alert, visible: false })}
        />
      )}

      <LoginRegisterModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
      />

      <VerifySwitchRoleModal
        open={showVerifyRoleModal}
        onClose={() => setShowVerifyRoleModal(false)}
        onConfirm={handleConfirmRoleSwitch}
        title="Activate Seller Account"
        description="To make an offer, you need an active seller account. By activating, you will be able to respond to buyer requests and make offers on listed products. Would you like to activate your seller account now?"
        confirmText="Yes, Activate My Account"
        cancelText="Not Now"
      />

      <style>{`
        /* Title styles */
        .page-main-title {
          margin: 0;
          color: ${hexToRgba(
        "#0f172a",
        0.95,
      )}; /* slightly darker than textPrimary */
          font-weight: 800;
          letter-spacing: -0.6px;
          font-size: 2.25rem; /* desktop */
          line-height: 1.05;
        }
        .page-main-subtitle {
          margin-top: ${theme.spacing ? theme.spacing(1) : "8px"};
          margin-bottom: 0;
          color: ${hexToRgba(textSecondary, 1)};
          font-size: 1rem;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Tabs wrapper: add gap and a visible divider below the tabs */
        .glass-tabs {
          padding-bottom: 18px; /* space between tabs and divider */
          border-bottom: 1px solid ${hexToRgba(
        "#e6e9ee",
        0.9,
      )}; /* divider below tabs */
          margin-bottom: 20px; /* separate from content */
        }
        .glass-tabs .nav-tabs {
          border: none;
          display: flex;
          justify-content: flex-start;
          margin-bottom: 0; /* ensure divider sits below tabs */
          gap: 0; /* we use nav-item margin for consistent spacing across browsers */
        }
        .glass-tabs .nav-item { margin-right: 14px; }

        /* Base tab styles */
        .glass-tabs .nav-link {
          border: none !important;
          border-radius: 9999px !important;
          padding: 10px 18px !important;
          font-weight: 600;
          font-size: 0.95rem;
          color: ${textSecondary};
          background: linear-gradient(180deg, ${hexToRgba(
        "#ffffff",
        0.7,
      )} 0%, ${hexToRgba("#ffffff", 0.5)} 100%);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid ${hexToRgba("#ffffff", 0.45)};
          box-shadow: inset 0 1px 0 ${hexToRgba(
        "#ffffff",
        0.5,
      )}, 0 6px 14px ${hexToRgba("#000000", 0.06)};
          transition: all 0.25s ease;
          text-decoration: none !important; /* remove underline */
        }
        .glass-tabs .nav-link:hover {
          transform: translateY(-1px);
          box-shadow: inset 0 1px 0 ${hexToRgba(
        "#ffffff",
        0.55,
      )}, 0 10px 18px ${hexToRgba("#000000", 0.1)};
        }

        /* Remove any pseudo-element underline (bootstrap or custom) */
        .glass-tabs .nav-link::after,
        .glass-tabs .nav-link.active::after {
          display: none !important;
          content: none !important;
        }

        /* Prevent bootstrap from adding an underline or border-bottom to the active link */
        .glass-tabs .nav-link.active,
        .glass-tabs .nav-link:focus,
        .glass-tabs .nav-link:active {
          text-decoration: none !important;
          border-bottom: none !important;
          outline: none !important;
          box-shadow: none !important; /* reset before applying our custom shadow */
        }

        /* Distinct active styles per tab (light warm glossy gradients) */
        .nav-link.tab-trades.active {
          color: #111 !important;
          background: linear-gradient(180deg, ${hexToRgba(
        "#fff3e0",
        0.95,
      )} 0%, ${hexToRgba("#ffe0b2", 0.95)} 100%);
          box-shadow: 0 8px 20px ${hexToRgba(
        "#ffd59a",
        0.18,
      )}, inset 0 1px 0 ${hexToRgba("#ffffff", 0.6)};
        }
        .nav-link.tab-bids.active {
          color: #111 !important;
          background: linear-gradient(180deg, ${hexToRgba(
        "#f7eef8",
        0.95,
      )} 0%, ${hexToRgba("#f0d9ff", 0.95)} 100%);
          box-shadow: 0 8px 20px ${hexToRgba(
        "#e8c8ff",
        0.14,
      )}, inset 0 1px 0 ${hexToRgba("#ffffff", 0.6)};
        }
        .nav-link.tab-offers.active {
          color: #111 !important;
          background: linear-gradient(180deg, ${hexToRgba(
        "#fff6f0",
        0.95,
      )} 0%, ${hexToRgba("#ffe8d5", 0.95)} 100%);
          box-shadow: 0 8px 20px ${hexToRgba(
        "#ffd6b3",
        0.16,
      )}, inset 0 1px 0 ${hexToRgba("#ffffff", 0.6)};
        }

        /* Pagination (custom) */
        .custom-pagination { display: inline-flex; gap: 8px; align-items:center; }
        .custom-pagination .page-item .page-link {
          border-radius: 9999px;
          padding: 6px 12px;
          min-width: 36px;
          text-align: center;
          background: ${hexToRgba("#ffffff", 0.65)};
          border: 1px solid ${hexToRgba("#ffffff", 0.6)};
          color: ${textSecondary};
          box-shadow: 0 6px 18px ${hexToRgba("#000000", 0.06)};
          transition: all 0.18s ease;
        }
        .custom-pagination .page-item .page-link:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px ${hexToRgba("#000000", 0.08)};
        }
        .custom-pagination .page-item.active .page-link {
          color: #111;
          background: linear-gradient(180deg, ${hexToRgba(
        "#fff3e0",
        0.95,
      )} 0%, ${hexToRgba("#ffe0b2", 0.95)} 100%);
          box-shadow: 0 10px 26px ${hexToRgba("#ffd59a", 0.18)};
          border: none;
          font-weight: 700;
        }
        .custom-pagination .page-item .page-link .svg-inline--fa { vertical-align: middle; }
        /* Bids variant */
        .custom-pagination[data-type="bids"] .page-item.active .page-link {
          background: linear-gradient(180deg, ${hexToRgba(
        "#f7eef8",
        0.95,
      )} 0%, ${hexToRgba("#f0d9ff", 0.95)} 100%);
          box-shadow: 0 10px 26px ${hexToRgba("#e8c8ff", 0.14)};
        }
        /* Offers variant */
        .custom-pagination[data-type="offers"] .page-item.active .page-link {
          background: linear-gradient(180deg, ${hexToRgba(
        "#fff6f0",
        0.95,
      )} 0%, ${hexToRgba("#ffe8d5", 0.95)} 100%);
          box-shadow: 0 10px 26px ${hexToRgba("#ffd6b3", 0.16)};
        }

        /* Wrapper for horizontal scrolling */
.table-wrapper {
  overflow-x: auto;
  width: 100%;
  padding-bottom: 8px; /* optional, to give some space for scrollbar */
}

/* Glassy Table */
.glass-table {
  width: 100%;
  border-collapse: separate; /* allows spacing between rows */
  border-spacing: 0 12px; /* vertical spacing between rows */
  table-layout: fixed; /* ensures even column widths */
  min-width: 600px; /* ensures scroll appears on small screens */
}

/* Header styles */
.glass-table thead th {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.85rem;
  border: none;
  color: ${textSecondary};
  background: transparent;
  padding: 16px 20px; /* breathing space inside headers */
  text-align: left;
  white-space: nowrap; /* prevents wrapping */
}

/* Body rows */
.glass-table tbody tr {
  background: linear-gradient(180deg, ${hexToRgba(
        "#ffffff",
        0.6,
      )} 0%, ${hexToRgba("#ffffff", 0.45)} 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 14px;
  box-shadow: 0 2px 6px ${hexToRgba(
        "#000000",
        0.05,
      )}, inset 0 1px 0 ${hexToRgba("#ffffff", 0.5)};
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  border: 1px solid ${hexToRgba("#ffffff", 0.5)};
}

/* Hover effect for rows */
.glass-table tbody tr:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px ${hexToRgba(
        "#000000",
        0.1,
      )}, inset 0 1px 0 ${hexToRgba("#ffffff", 0.6)};
  background: linear-gradient(180deg, ${hexToRgba(
        "#ffffff",
        0.7,
      )} 0%, ${hexToRgba("#ffffff", 0.5)} 100%);
}

/* Table cells */
.glass-table td {
  border: none !important;
  padding: 14px 20px !important; /* more horizontal breathing space */
  vertical-align: middle;
  font-size: 0.95rem;
  color: #000000;
  white-space: nowrap; /* prevents wrapping */
  text-overflow: ellipsis; /* adds ... if text is too long */
  overflow: hidden;
}

/* Make rows clickable if needed */
.glass-table .clickable-row {
  cursor: pointer;
}

/* Optional: scroll style for modern look */
.table-wrapper::-webkit-scrollbar {
  height: 8px;
}
.table-wrapper::-webkit-scrollbar-thumb {
  background: ${hexToRgba("#000000", 0.2)};
  border-radius: 4px;
}
.table-wrapper::-webkit-scrollbar-track {
  background: transparent;
}


        .glass-card {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }
        .glass-card .card-header-gradient {
          position: absolute;
          inset: -1px -1px auto -1px;
          height: 140px;
          background: radial-gradient(600px circle at 0% 0%, ${hexToRgba(
        primary,
        0.18,
      )} 0, transparent 60%),
                      radial-gradient(600px circle at 100% 0%, ${hexToRgba(
        secondary,
        0.2,
      )} 0, transparent 60%);
          pointer-events: none;
          z-index: -1;
          filter: blur(10px);
        }
        .hoverable-tooltip-wrapper {
          display: contents !important;
         }
        /* Responsive tweaks */
        @media (max-width: 1024px) {
          .page-main-title { font-size: 2rem; }
          .page-main-subtitle { font-size: 0.98rem; }
          .glass-card { padding: ${theme.spacing ? theme.spacing(4) : "16px"}; }
        }
        @media (max-width: 640px) {
          .page-main-title { font-size: 1.5rem; }
          .page-main-subtitle { font-size: 0.9rem; }
          .card-tab-header { flex-direction: column; align-items: flex-start; gap: 8px; }
          .glass-tabs .nav-item { margin-right: 10px; }
          .glass-tabs .nav-link { padding: 8px 12px !important; font-size: 0.9rem; }
          .glass-card { min-height: 480px; padding: ${theme.spacing ? theme.spacing(3) : "12px"
        }; }
          .custom-pagination { gap: 6px; }
          .custom-pagination .page-item .page-link { padding: 6px 8px; min-width: 32px; }
        }
      `}</style>
    </>
  );
};

export default Market;
